import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { peopleAPI } from '../services/api';
import PersonDetailsDialog from '../components/PersonDetailsDialog';

interface Person {
  id: number;
  name: string;
  city?: string;
  notes?: string;
  shared_count?: number;
  completed_count?: number;
  created_at: string;
}

interface PersonForm {
  name: string;
  city: string;
  notes: string;
}

const PeoplePage = () => {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [form, setForm] = useState<PersonForm>({
    name: '',
    city: '',
    notes: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Person Details State
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);

  // Sort & Filter States
  const [filterText, setFilterText] = useState('');
  const [sortField, setSortField] = useState<keyof Person>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchPeople();
  }, []);

  const handleSort = (field: keyof Person) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc'); // Default asc for new field? Usually desc for counts/dates better.
      if (field === 'shared_count' || field === 'completed_count' || field === 'created_at') {
        setSortDirection('desc');
      }
    }
  };

  const handlePersonClick = (person: Person) => {
    setSelectedPerson(person);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedPerson(null);
  };

  const sortedPeople = people
    .filter(p =>
      p.name.toLowerCase().includes(filterText.toLowerCase()) ||
      (p.city && p.city.toLowerCase().includes(filterText.toLowerCase()))
    )
    .sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue === bValue) return 0;
      if (aValue === undefined) return 1;
      if (bValue === undefined) return -1;

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const fetchPeople = async () => {
    try {
      const response = await peopleAPI.getAll();
      setPeople(response.data);
    } catch (error) {
      console.error('Kişiler alınırken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (person?: Person) => {
    if (person) {
      setEditingPerson(person);
      setForm({
        name: person.name,
        city: person.city || '',
        notes: person.notes || '',
      });
    } else {
      setEditingPerson(null);
      setForm({
        name: '',
        city: '',
        notes: '',
      });
    }
    setFormErrors({});
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingPerson(null);
    setForm({ name: '', city: '', notes: '' });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!form.name.trim()) {
      errors.name = 'Ad zorunludur';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (editingPerson) {
        await peopleAPI.update(editingPerson.id, form);
      } else {
        await peopleAPI.create(form);
      }

      handleCloseDialog();
      fetchPeople();
    } catch (error: any) {
      console.error('Kişi kaydedilirken hata:', error);
      if (error.response?.data?.error) {
        setFormErrors({ name: error.response.data.error });
      }
    }
  };

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [personToDelete, setPersonToDelete] = useState<number | null>(null);

  const handleOpenDeleteDialog = (id: number) => {
    setPersonToDelete(id);
    setDeleteOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteOpen(false);
    setPersonToDelete(null);
  };

  const confirmDeletePerson = async () => {
    if (!personToDelete) return;

    try {
      await peopleAPI.delete(personToDelete);
      fetchPeople();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Kişi silinirken hata:', error);
      alert('Kişi silinirken bir hata oluştu'); // Kullanıcıya en azından bir feedback verelim
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Kişiler
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Yeni Kişi
        </Button>
      </Box>

      <Box mb={2}>
        <TextField
          label="Ara (İsim, Şehir)"
          variant="outlined"
          fullWidth
          size="small"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
      </Box>

      <Paper elevation={0}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => handleSort('name')}>
                  Ad {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => handleSort('city')}>
                  Şehir {sortField === 'city' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => handleSort('shared_count')}>
                  Paylaşılan {sortField === 'shared_count' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => handleSort('completed_count')}>
                  Tamamlanan {sortField === 'completed_count' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell>Notlar</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPeople.map((person) => (
                <TableRow key={person.id} hover>
                  <TableCell onClick={() => handlePersonClick(person)} style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }}>
                    <Box display="flex" alignItems="center">
                      <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                      {person.name}
                    </Box>
                  </TableCell>
                  <TableCell>{person.city || '-'}</TableCell>
                  <TableCell align="center">{person.shared_count || 0}</TableCell>
                  <TableCell align="center">{person.completed_count || 0}</TableCell>
                  <TableCell>
                    {person.notes ? (
                      <Tooltip title={person.notes} placement="top">
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {person.notes}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Düzenle">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => { e.stopPropagation(); handleOpenDialog(person); }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sil">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => { e.stopPropagation(); handleOpenDeleteDialog(person.id); }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {sortedPeople.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      Kayıt bulunamadı.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPerson ? 'Kişi Düzenle' : 'Yeni Kişi Ekle'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              label="Ad"
              fullWidth
              value={form.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
              error={!!formErrors.name}
              helperText={formErrors.name}
              margin="normal"
            />
            <TextField
              label="Şehir"
              fullWidth
              value={form.city}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, city: e.target.value })}
              margin="normal"
            />
            <TextField
              label="Notlar"
              fullWidth
              multiline
              rows={3}
              value={form.notes}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, notes: e.target.value })}
              margin="normal"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingPerson ? 'Güncelle' : 'Ekle'}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Kişi Sil</DialogTitle>
        <DialogContent>
          <Typography>Bu kişiyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>İptal</Button>
          <Button onClick={confirmDeletePerson} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Person Details Dialog */}
      <PersonDetailsDialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        personId={selectedPerson?.id || null}
        personName={selectedPerson?.name || ''}
      />
    </>
  );
};

export default PeoplePage;